import { CORE_STAT_IDS, SKILL_IDS, type BackgroundDef } from '../../../lib/skills/types';
import { backgroundDefaults } from '../../../lib/skills/defaults';
import { STAT_IDS, STAT_MAX, statLabel } from '../../../lib/hb/types';
import { issuesUnder, type Issue } from '../../../lib/validate';
import { Fieldset, IssueList, NumberField, NumberInput, TextField } from '../fields';
import type { FormProps } from '../types';

/*
 * ADR 0066 retired the ADR 0044 stats section form (caps, training, combat knobs, perks): the Outta-Ten forms are in
 * components/forms/skills/SkillForms.tsx. This file keeps the stat map editor (condition statMods) and the background form.
 */

/**
 * Stat id → whole number (a background's bias, a condition's statMods): one input per stat, blank = not listed. Keys that aren't
 * stats stay in the map (the validator reports them) and show as a note.
 */
export function StatMapEditor({
  label,
  hint,
  value,
  onChange,
  issues,
  min = -STAT_MAX,
  max = STAT_MAX,
}: {
  label: string;
  hint?: string;
  value: Record<string, number> | null | undefined;
  onChange: (next: Record<string, number>) => void;
  issues?: Issue[];
  min?: number;
  max?: number;
}) {
  const map = value ?? {};
  const unknown = Object.keys(map).filter((k) => !(STAT_IDS as readonly string[]).includes(k));
  const set = (stat: string, v: number | null) => {
    const next = { ...map };
    if (v == null) delete next[stat];
    else next[stat] = v;
    onChange(next);
  };
  return (
    <div className="sm:col-span-2">
      <span className="text-xs font-semibold uppercase tracking-wide">{label}</span>
      <div className="mt-1 grid grid-cols-3 gap-2 sm:grid-cols-6">
        {STAT_IDS.map((s) => (
          <label key={s} className="text-xs font-semibold">
            {statLabel(s)}
            <NumberInput
              label={`${label} ${statLabel(s)}`}
              integer
              nullable
              min={min}
              max={max}
              value={map[s]}
              onChange={(v) => set(s, v)}
            />
          </label>
        ))}
      </div>
      {unknown.length > 0 && (
        <p className="mt-0.5 text-xs text-blam">Not a stat: {unknown.join(', ')} (edit in JSON).</p>
      )}
      {hint && <p className="mt-0.5 text-xs text-bruise">{hint}</p>}
      <IssueList issues={issues} />
    </div>
  );
}

/**
 * Bias keys (`stat:<id>` -3..3 on top of 5, `skill:<id>` 0..5 on top of the skill's baseline) as one input each; blank = not listed.
 * Keys that aren't sheet owners stay in the map (the validator reports them) and show as a note.
 */
function BiasEditor({
  value,
  onChange,
  issues,
}: {
  value: Record<string, number> | null | undefined;
  onChange: (next: Record<string, number>) => void;
  issues?: Issue[];
}) {
  const map = value ?? {};
  const owners = [...CORE_STAT_IDS.map((s) => `stat:${s}`), ...SKILL_IDS.map((s) => `skill:${s}`)];
  const unknown = Object.keys(map).filter((k) => !owners.includes(k));
  const set = (k: string, v: number | null) => {
    const next = { ...map };
    if (v == null) delete next[k];
    else next[k] = v;
    onChange(next);
  };
  const cell = (k: string, label: string, min: number, max: number) => (
    <label key={k} className="text-xs font-semibold">
      {label}
      <NumberInput
        label={`Bias ${label}`}
        integer
        nullable
        min={min}
        max={max}
        value={map[k]}
        onChange={(v) => set(k, v)}
      />
    </label>
  );
  return (
    <div className="sm:col-span-2">
      <span className="text-xs font-semibold uppercase tracking-wide">
        Bias: stats (-3..3 on top of 5)
      </span>
      <div className="mt-1 grid grid-cols-3 gap-2 sm:grid-cols-6">
        {CORE_STAT_IDS.map((s) => cell(`stat:${s}`, statLabel(s), -3, 3))}
      </div>
      <span className="mt-2 block text-xs font-semibold uppercase tracking-wide">
        Bias: skills (0..5 on top of the baseline)
      </span>
      <div className="mt-1 grid grid-cols-3 gap-2 sm:grid-cols-6">
        {SKILL_IDS.map((s) => cell(`skill:${s}`, s, 0, 5))}
      </div>
      {unknown.length > 0 && (
        <p className="mt-0.5 text-xs text-blam">
          Not a stat or skill: {unknown.join(', ')} (edit in JSON).
        </p>
      )}
      <IssueList issues={issues} />
    </div>
  );
}

/**
 * A recruit background (`backgrounds[]`, ADR 0066 §2): INTERNAL ONLY. It biases a recruit's starting sheet and picks the jacket; it is
 * never shown to players and is not a role.
 */
export function BackgroundForm({ value, onChange, errors }: FormProps<BackgroundDef>) {
  const g = { ...backgroundDefaults(), ...value };
  return (
    <>
      <p className="mb-3 rounded border-2 border-ink bg-caption/40 p-2 text-xs">
        Internal only: players never see a background. It biases a recruit&apos;s starting stats and
        skills and picks the jacket. It is not a role.
      </p>
      <Fieldset legend="Background">
        <TextField
          label="Id"
          mono
          value={g.id}
          issues={errors.id}
          onChange={(v) => onChange({ ...g, id: v })}
        />
        <TextField
          label="Jacket"
          mono
          hint="Jacket look id (LookDresser)."
          value={g.jacket}
          onChange={(v) => onChange({ ...g, jacket: v })}
        />
        <NumberField
          label="Weight"
          hint="Pick weight among backgrounds; 0 = never picked."
          integer
          min={0}
          value={g.weight}
          issues={errors.weight}
          onChange={(v) => onChange({ ...g, weight: v ?? 0 })}
        />
        <TextField
          label="Legacy role"
          mono
          hint="The old recruit role id this background replaces (save migration). Unique."
          value={g.legacyRole}
          issues={errors.legacyRole}
          onChange={(v) => onChange({ ...g, legacyRole: v })}
        />
        <BiasEditor
          value={g.bias}
          issues={issuesUnder(errors, 'bias')}
          onChange={(bias) => onChange({ ...g, bias })}
        />
      </Fieldset>
    </>
  );
}
